import {
  Box,
  Button,
  FormHelperText,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { ChromePicker } from 'react-color'
import { FieldError, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { FileUploader, IconButton, Resource } from 'utoniq-ui'

import {
  ArtistDigitalMembershipCard,
  Maybe,
  UserDigitalMembershipCard,
} from '@/clients/utoniq-core/schema'
import { DigitalMembershipCardPreview } from '@/components/organisms/digitalMembershipCard'
import {
  Form,
  handleError,
  tLabel,
  useForm,
  UseFormProps,
  useMessage,
  useTranslation,
} from '@/core'
import { Cell, CellFrame, Organism, OrganismFrame } from '@/core/architecture'

export type MembershipCardEditFormProps = {
  artistId: string
  membershipCard: Maybe<
    Pick<
      UserDigitalMembershipCard,
      'cardDesignPath' | 'textColor' | 'displayAreaColor'
    >
  >
  fileUploadInfo: FileUploadInfo
  onSubmit: (values: EditMembershipCardValue) => Promise<void>
}

export type EditMembershipCardValue = ArtistDigitalMembershipCard & {
  cardDesignResource: Resource | null
}

export type FileUploadInfo = {
  onDrop: () => Promise<Resource | undefined>
  progress: number
}

const schema: UseFormProps['schema'] = o => ({
  textColor: o
    .string()
    .max(9)
    .label(tLabel('consoleDigitalMembershipCardDesignEdit.textColor')),
  displayAreaColor: o
    .string()
    .max(9)
    .optional()
    .allow(null, '')
    .label(tLabel('consoleDigitalMembershipCardDesignEdit.displayAreaColor')),
  cardDesignPath: o
    .string()
    .label(tLabel('consoleDigitalMembershipCardDesignEdit.cardDesign')),
  cardDesignResource: o.object().allow(null).optional(),
})

export const MembershipCardEditForm: Organism<MembershipCardEditFormProps> = ({
  artistId,
  onSubmit,
  fileUploadInfo,
  membershipCard,
}) => {
  const { t } = useTranslation('consoleDigitalMembershipCardDesignEdit')
  const [error, setError] = useState<string>('')
  const { showError } = useMessage()
  const {
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
    register,
    setValue,
    control,
    watch,
    reset,
  } = useForm<EditMembershipCardValue>({
    schema,
    defaultValues,
  })

  useEffect(() => {
    reset({
      displayAreaColor: membershipCard?.displayAreaColor ?? '',
      textColor: membershipCard?.textColor ?? '#FFFFFF',
      cardDesignPath: membershipCard?.cardDesignPath ?? '',
      cardDesignResource: membershipCard?.cardDesignPath
        ? {
            id: '',
            resourceName: '',
            imageUrl: membershipCard.cardDesignPath,
            imageUploadPath: membershipCard.cardDesignPath,
          }
        : null,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipCard])

  const handleOnSubmit = useCallback(
    async (values: EditMembershipCardValue) => {
      const inputs = {
        ...values,
      }
      await onSubmit(inputs).catch(error => {
        setError(error.message)
        handleError<EditMembershipCardValue>(error, setError, showError)
      })
    },
    [onSubmit, showError]
  )

  return (
    <OrganismFrame sx={{ py: 5 }}>
      <Form
        onSubmit={handleSubmit(handleOnSubmit)}
        id="membership-card-edit-form"
      >
        <CardDesignInputCell
          watch={watch}
          setValue={setValue}
          error={errors.cardDesignPath}
          fileUploadInfo={fileUploadInfo}
        />
        <Box sx={{ mt: 4 }}>
          <ColorPickerCell
            label={t('textColor')}
            value={watch('textColor')}
            onChange={color => setValue('textColor', color)}
            error={errors?.textColor}
          />
        </Box>
        <Box sx={{ mt: 4 }}>
          <ColorPickerCell
            label={t('displayAreaColor')}
            value={watch('displayAreaColor') ?? ''}
            onChange={color => setValue('displayAreaColor', color)}
            onClear={() => setValue('displayAreaColor', '')}
            error={errors?.displayAreaColor}
          />
        </Box>

        <Box sx={{ mt: 6 }}>
          <DigitalMembershipCardPreview
            digitalMembershipCard={{
              cardDesignPath: watch('cardDesignPath'),
              textColor: watch('textColor'),
              displayAreaColor: watch('displayAreaColor') ?? '',
            }}
            loading={isSubmitting}
            hideMask={true}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, textAlign: 'center' }}
          >
            {t('caption')}
          </Typography>
        </Box>

        <Stack alignItems="center" sx={{ mt: 6, mb: 6 }}>
          <Button
            loading={isSubmitting}
            type="submit"
            size="large"
            sx={{ width: 244 }}
            disabled={!isValid || isSubmitting}
          >
            {t('register')}
          </Button>
        </Stack>
      </Form>
    </OrganismFrame>
  )
}

const defaultValues: EditMembershipCardValue = {
  cardDesignPath: '',
  cardDesignResource: null,
  textColor: '',
  displayAreaColor: '',
}

export const ColorPickerCell = ({
  label,
  value,
  onChange,
  onClear,
  error,
}: {
  label: string
  value: string
  onChange: (color: string) => void
  onClear?: () => void
  error?: FieldError
}) => {
  const { t } = useTranslation('consoleDigitalMembershipCardDesignEdit')
  const [displayColorPicker, setDisplayColorPicker] = useState<boolean>(false)
  const handleClose = () => {
    setDisplayColorPicker(false)
  }

  const handleColorChange = (color: string) => {
    onChange(color)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && displayColorPicker) {
      // カラーピッカーが開いている状態でエンターキー押下でサブミット処理が走らないようにする
      e.preventDefault()
      e.stopPropagation()
      setDisplayColorPicker(false)
    }
  }

  return (
    <>
      <CellFrame>
        <Box>
          <Typography variant="h5" color="text.primary" sx={{ mb: 1 }}>
            {label}
          </Typography>
          <TextField
            fullWidth
            value={value || ''}
            placeholder={t('colorPlaceholder')}
            slotProps={{
              input: {
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: value || '#ffffff',
                        border: '1px solid #ccc',
                        mr: 1,
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment:
                  onClear && value ? (
                    <InputAdornment position="end">
                      <Box
                        sx={{
                          backgroundColor: '#E5ECEF',
                          borderRadius: '50%',
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          '&:hover': {
                            backgroundColor: '#d0d0d0',
                          },
                        }}
                      >
                        <IconButton.Trash
                          onClick={e => {
                            e.stopPropagation()
                            onClear()
                          }}
                          size="small"
                        />
                      </Box>
                    </InputAdornment>
                  ) : undefined,
              },
            }}
            onClick={() => setDisplayColorPicker(!displayColorPicker)}
            onKeyDown={handleKeyDown}
            sx={{
              cursor: 'pointer',
              '& .MuiInputBase-input': {
                cursor: 'pointer',
              },
            }}
          />
          {error && <FormHelperText error>{error.message}</FormHelperText>}
        </Box>
        {displayColorPicker && (
          <Box sx={{ position: 'absolute', zIndex: 2000 }}>
            <Box
              sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
              onClick={handleClose}
            />
            <Box onKeyDown={handleKeyDown}>
              <ChromePicker
                color={value || '#ffffff'}
                onChange={color => handleColorChange(color.hex)}
              />
            </Box>
          </Box>
        )}
      </CellFrame>
    </>
  )
}

type CardDesignInputCellProps = {
  watch: UseFormWatch<EditMembershipCardValue>
  fileUploadInfo: FileUploadInfo
  setValue: UseFormSetValue<EditMembershipCardValue>
  error?: FieldError
}

export const CardDesignInputCell: Cell<CardDesignInputCellProps> = ({
  watch,
  setValue,
  fileUploadInfo,
  error,
}) => {
  const { t } = useTranslation('consoleDigitalMembershipCardDesignEdit')
  return (
    <CellFrame sx={{ pb: 8 }}>
      <FileUploader
        sx={{ p: 0, width: '100%' }}
        shape="card"
        resource={watch('cardDesignResource') as Resource | undefined}
        t={t}
        fileUploadInfo={{
          type: 'image',
          async onDrop() {
            const resource = await fileUploadInfo.onDrop()
            if (!resource) {
              return
            }
            setValue('cardDesignPath', resource.imageUrl as never, {
              shouldValidate: true,
            })
            setValue(
              'cardDesignResource',
              { ...resource },
              { shouldValidate: true }
            )
            return resource
          },
          progress: fileUploadInfo.progress || 0,
          description: t('cardDesignUploaderDescription'),
          onClose() {
            setValue('cardDesignResource', null, {
              shouldDirty: true,
              shouldValidate: true,
            })
            setValue('cardDesignPath', '', {
              shouldDirty: true,
              shouldValidate: true,
            })
          },
        }}
        content={{ title: t('cardDesign') }}
      />
      {!!error && <FormHelperText error>{error.message}</FormHelperText>}
    </CellFrame>
  )
}
